import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getAppCategories } from "./appsConfig";
import type { AppCategory, AppItem } from "./types";
import { isCategoryEnabled, isAppEnabled } from "../../../config/appsConfig";
import { useAccess } from "../../../hooks/useAccess";
import { isOwnerRoleId, isPlatformAdminRoleId } from "../../../hooks/useIsReseller";
import { isDeveloperRoleId } from "../../../hooks/useIsFlagsOperator";
import { useEntitlements } from "../../../hooks/useEntitlement";
import { filterByEntitlements } from "./filterByEntitlements";
import { useExpensesAccess } from "../../../Layouts/expenses/hooks/useExpensesAccess";
import { useTeamAccess } from "../../../Layouts/team/hooks/useTeamAccess";
import { relocateCobranza } from "./relocateCobranza";
import { isCobranzaOperatorUser, isFinanceRole } from "../../../hooks/useIsCobranzaOperator";

/** Apps de Finanzas internas que comparten la lista de acceso de Gastos (CC-CONSOLE #615, #1255). */
const FINANCE_ACCESS_APPS = ["expenses", "finance-settings"];

/**
 * Developer (épica #1219): en Sistemas Internos sólo ve la app Feature Flags.
 * El resto de las apps internas sigue siendo de la plataforma.
 */
export const DEVELOPER_INTERNAL_APPS = ["feature-flags"];

/**
 * Apps de Sistemas Internos que NO pasan por el motor de accesos: su ruta la
 * protege el rol (owner / admin reseller) y no un nodo de acceso.
 *
 * El acceso de un rol se guarda en Firestore como lista explícita de nodos
 * (`roles/{id}/permissions/navigation`, sembrada con seed-role-access). Una
 * app que nace DESPUÉS de la siembra no está en esa lista, así que el admin
 * reseller no vería su mosaico hasta que alguien volviera a sembrar o la
 * marcara a mano en Accesos — aunque la ruta sí lo deja entrar. Las de esta
 * lista se muestran a la administración de la plataforma sin ese paso.
 */
// `movik` sigue aquí aunque su mosaico vive hoy en B2C (categoría entera de
// plataforma, abajo): conserva la llave `app:movik` y el derecho que ya tenía.
export const PLATFORM_ROLE_APPS = ["comportamiento", "movik"];

/**
 * Categorías enteras de la administración de la plataforma (B2C → Movik): nacen
 * `internal` en el catálogo de derechos y ningún perfil de acceso las nombra;
 * se muestran a esos roles sin sembrarlas, como PLATFORM_ROLE_APPS.
 */
export const PLATFORM_ROLE_CATEGORIES = ["b2c"];

export function useFilteredCategories(): AppCategory[] {
  const { t } = useTranslation("common");
  const categories = getAppCategories(t);

  const { isEnforced, isResolved, canAccessCategory, canAccessApp } =
    useAccess();

  // Banderas globales (épica #1219): una categoría o app en `off` desaparece
  // para todos; en `internal`, sólo la ven los operadores. Sin respuesta del
  // backend no se oculta nada.
  const { can: canUseFlag } = useEntitlements();

  // Gastos y Presupuestos (y su Configuración): solo para la lista de acceso del
  // módulo. Se pregunta al servidor solo si el tenant tiene la categoría.
  const expensesAccess = useExpensesAccess(isCategoryEnabled("finanzas") && isAppEnabled("finanzas", "expenses"));

  // Equipo (CC-CONSOLE #1145): nadie lo ve hasta que el servidor responde quién
  // lo administra. Se pregunta solo si el tenant tiene la app habilitada.
  const teamAccess = useTeamAccess(isCategoryEnabled("gestion-staff") && isAppEnabled("gestion-staff", "team"));

  return useMemo(() => {
    if (!isResolved) return [];

    // App con `requiredRoleIds`: solo para esos roles (p. ej. la vista del
    // cliente desde CC-CONSOLE, solo owner / admin reseller). Sin el campo,
    // todos. El owner pasa siempre.
    const roleId = localStorage.getItem("userRoleId") || "";
    const byRole = (app: AppItem) =>
      !app.requiredRoleIds || isOwnerRoleId(roleId) || app.requiredRoleIds.includes(roleId);
    const byDeveloper = (categoryId: string, app: AppItem) =>
      !isDeveloperRoleId(roleId) || categoryId !== "internos" || DEVELOPER_INTERNAL_APPS.includes(app.id);
    // Lo que decide el servidor: mientras no confirma, no se pinta el mosaico.
    const byServerAccess = (app: AppItem) => {
      if (FINANCE_ACCESS_APPS.includes(app.id)) return expensesAccess === "allowed";
      if (app.id === "team") return teamAccess === "allowed";
      return true;
    };

    const byFlags = filterByEntitlements(
      categories
        .filter((category) => isCategoryEnabled(category.id))
        .map((category) => ({
          ...category,
          apps: category.apps.filter(
            (app) =>
              isAppEnabled(category.id, app.id) && byRole(app) && byDeveloper(category.id, app) && byServerAccess(app),
          ),
        }))
        .filter((category) => category.apps.length > 0),
      canUseFlag,
    );

    // Cobranza (CC-CONSOLE #1243): donde el tenant la tiene en «Finanzas internas»
    // ya no se repite en «Sistemas Internos», y conserva la puerta que heredaba de
    // esa categoría: sólo la administración de la plataforma.
    // Cobranza (CC-CONSOLE #1264): también la opera el rol finance.
    const byTenant = relocateCobranza(byFlags, isPlatformAdminRoleId(roleId) || isCobranzaOperatorUser(roleId));

    if (!isEnforced) return byTenant;

    const byPlatformRole = (categoryId: string, app: AppItem) =>
      (categoryId === "internos" && PLATFORM_ROLE_APPS.includes(app.id) && isPlatformAdminRoleId(roleId)) ||
      (PLATFORM_ROLE_CATEGORIES.includes(categoryId) && isPlatformAdminRoleId(roleId)) ||
      // El mosaico de Cobranza al rol finance se lo da su ROL, no sus perfiles de acceso.
      (app.id === "cobranza" && isFinanceRole(roleId));

    return byTenant
      .filter(
        (category) =>
          canAccessCategory(category.id) ||
          (PLATFORM_ROLE_CATEGORIES.includes(category.id) && isPlatformAdminRoleId(roleId)),
      )
      .map((category) => ({
        ...category,
        apps: category.apps.filter(
          (app) => byPlatformRole(category.id, app) || canAccessApp(category.id, app.id),
        ),
      }))
      .filter((category) => category.apps.length > 0);
  }, [categories, isEnforced, isResolved, canAccessCategory, canAccessApp, canUseFlag, expensesAccess, teamAccess]);
}
